import React, { useContext } from 'react';
import { styled } from '@mui/material';
import { LegendType } from './Chart.types';

export const LegendTypeDataSelector = {
  [LegendType.linearTop]: `[data-legend-type="${LegendType.linearTop}"]`,
  [LegendType.linearBottom]: `[data-legend-type="${LegendType.linearBottom}"]`,
  [LegendType.tableRight]: `[data-legend-type="${LegendType.tableRight}"]`,
  [LegendType.none]: `[data-legend-type="${LegendType.none}"]`,
} as const;

const StyledWrapper = styled('div')({
  height: '100%',
  width: '100%',
  overflow: 'hidden',

  [`&:not(${LegendTypeDataSelector[LegendType.none]})`]: {
    display: 'grid',
  },

  [`&${LegendTypeDataSelector[LegendType.linearTop]}`]: {
    gridTemplateAreas: "'legend' 'chart'",
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto 1fr',
  },

  [`&${LegendTypeDataSelector[LegendType.linearBottom]}`]: {
    gridTemplateAreas: "'chart' 'legend'",
    gridTemplateColumns: '1fr',
    gridTemplateRows: '1fr auto',
  },

  [`&${LegendTypeDataSelector[LegendType.tableRight]}`]: {
    gridTemplateAreas: "'chart legend'",
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr',
    alignContent: 'center',
  },
});

export type ChartWrapperProps = {
  legendType: LegendType;
};

type ChartWrapperContextType = {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
};

const ChartWrapperContext = React.createContext<ChartWrapperContextType | undefined>(undefined);

export const ChartWrapper = ({ children, legendType }: React.PropsWithChildren<ChartWrapperProps>) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  return (
    <ChartWrapperContext.Provider value={React.useMemo(() => ({ wrapperRef }), [])}>
      <StyledWrapper ref={wrapperRef} data-legend-type={legendType}>
        {children}
      </StyledWrapper>
    </ChartWrapperContext.Provider>
  );
};

export const useChartWrapperContext = () => {
  const ctx = useContext(ChartWrapperContext);
  if (!ctx) throw new Error('useChartWrapperContext must be used within a ChartWrapper');
  return ctx;
};
